import React, { useEffect, useState } from 'react';
import { bool, func, string } from 'prop-types';
import { Elements } from '@stripe/react-stripe-js';

import { getStripePromise } from '../../util/stripeEmbedded';
import StripePaymentElementForm from '../StripePaymentElementForm/StripePaymentElementForm';

import css from './StripePaymentModal.module.css';

/**
 * Shared modal wrapper used by Flows 3 (listing fee), 4 (boost) and 5
 * (subscription). It owns the <Elements> provider — the PaymentElement needs
 * Elements to be initialised with the payment's clientSecret, which the
 * app-level provider does not have — and renders StripePaymentElementForm
 * inside it.
 *
 * Layout: full-viewport backdrop with a centred card on desktop and a
 * full-screen bottom sheet on mobile (≤768px). Closing mid-payment asks for
 * confirmation so an in-flight payment isn't abandoned by accident.
 */
const StripePaymentModal = props => {
  const {
    isOpen,
    clientSecret,
    onClose,
    onSuccess,
    returnUrl,
    submitLabel,
    title,
  } = props;

  const [isPaymentInFlight, setIsPaymentInFlight] = useState(false);

  // BR-06: when a 3DS redirect flow returns to Wheelplace, the return_url
  // carries ?payment_status=complete. Treat that as success and strip the
  // param so a refresh doesn't re-trigger it.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment_status') === 'complete') {
      // On a 3DS full-redirect return, Stripe also appends payment_intent /
      // redirect_status — forward the id so onSuccess can verify the payment.
      const redirectedPaymentIntentId = params.get('payment_intent');
      params.delete('payment_status');
      params.delete('payment_intent');
      params.delete('payment_intent_client_secret');
      params.delete('redirect_status');
      const newSearch = params.toString();
      const newUrl =
        window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
      window.history.replaceState({}, document.title, newUrl);
      if (onSuccess) {
        onSuccess(redirectedPaymentIntentId ? { id: redirectedPaymentIntentId } : undefined);
      }
    }
    // Run once on mount.
  }, []);

  const handleRequestClose = () => {
    if (isPaymentInFlight) {
      const discard = window.confirm('Discard payment?');
      if (!discard) {
        return;
      }
    }
    setIsPaymentInFlight(false);
    if (onClose) {
      onClose();
    }
  };

  if (!isOpen || !clientSecret) {
    return null;
  }

  const options = {
    clientSecret,
    appearance: { theme: 'stripe' },
  };

  return (
    <div className={css.overlay} role="dialog" aria-modal="true" aria-label={title || 'Payment'}>
      <div className={css.backdrop} onClick={handleRequestClose} />
      <div className={css.sheet}>
        <div className={css.header}>
          {title ? <h2 className={css.title}>{title}</h2> : <span />}
          <button
            type="button"
            className={css.close}
            onClick={handleRequestClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className={css.body}>
          <Elements stripe={getStripePromise()} options={options}>
            <StripePaymentElementForm
              clientSecret={clientSecret}
              returnUrl={returnUrl}
              submitLabel={submitLabel}
              onSuccess={paymentIntent => {
                setIsPaymentInFlight(false);
                if (onSuccess) {
                  onSuccess(paymentIntent);
                }
              }}
              onError={() => setIsPaymentInFlight(false)}
              // While confirmPayment is running the payment is in-flight, so
              // the close button asks "Discard payment?" before closing.
              onProcessingChange={setIsPaymentInFlight}
              isLoading={false}
            />
          </Elements>
        </div>
      </div>
    </div>
  );
};

StripePaymentModal.defaultProps = {
  isOpen: false,
  clientSecret: null,
  onClose: null,
  onSuccess: null,
  returnUrl: null,
  submitLabel: 'Pay',
  title: null,
};

StripePaymentModal.propTypes = {
  isOpen: bool,
  clientSecret: string,
  onClose: func,
  onSuccess: func,
  returnUrl: string,
  submitLabel: string,
  title: string,
};

export default StripePaymentModal;
